/* eslint-disable no-inner-declarations */
/**
 * Contains HM Land Registry data © Crown copyright and database right 2017. This data is licensed under the Open Government Licence v3.0.
 */
import 'core-js/stable/url'
import 'core-js/stable/url-search-params'
import React from 'react'
import {
  axisBottom,
  axisRight,
  event,
  format as d3format,
  interpolateDate,
  line,
  scaleTime,
  scaleLinear,
  select
} from 'd3'
import {
  addDays,
  differenceInMonths,
  format,
  lastDayOfMonth,
  parse,
  startOfMonth,
  subMonths,
  getDaysInMonth
} from 'date-fns'
import useResizeObserver from '@utilities/useResizeObserver'
import { formatCurrency } from '@utilities/numbers'

const formatPercentage = d3format('.1%')
const parseDate = (v) => {
  return parse(v['ukhpi:refMonth']['@value'], 'yyyy-MM', 0)
}

const pcls = (className, t, c) => (c === t ? className : '')

function useRegionOfPostcode(postcode, setHidden) {
  const [region, setRegion] = React.useState({ admin_district: '' })
  React.useEffect(() => {
    if (postcode && postcode.length > 0) {
      fetch(`https://api.postcodes.io/postcodes/${postcode}`)
        .then((resp) => resp.json())
        .then((data) => {
          if (data?.status === 200) {
            //docs says any other status is error
            const {
              result: { admin_district }
            } = data
            setRegion((s) => ({ ...s, admin_district }))
          } else {
            setHidden(true)
            return Promise.reject(data)
          }
        })
    }
  }, [postcode])
  return region
}

const froms = {
  all_data: new Date(1995, 0, 1), // at the time of writing, earliest date with data
  // if the lag is longer than 6 months, the data might not be relevant anyway
  get last_year() {
    return subMonths(new Date(), 18)
  },
  get last_5years() {
    return subMonths(new Date(), 66)
  }
}
function useUKHPIDataItems(admin_district, selected, setTimeRange, setHidden) {
  const [items, setItems] = React.useState(null)
  React.useEffect(() => {
    if (admin_district) {
      const to = new Date()
      const from = froms[selected]
      const districtName = admin_district.replace(/\W/g, '-').toLowerCase()

      const url = new URL('https://landregistry.data.gov.uk/app/ukhpi/download/new.json')
      const params = url.searchParams
      const location = `http://landregistry.data.gov.uk/id/region/${districtName}`
      params.append('location', location)

      params.append('from', format(from, 'yyyy-MM-dd'))
      params.append('to', format(to, 'yyyy-MM-dd'))

      fetch(url)
        .then((resp) => resp.json())
        .then((data) => {
          if (data?.items != null && data.items.length > 0) {
            const ls = { last_year: -12, last_5years: -60, all_data: -data.items.length }[selected]
            const items = data.items.slice(ls)
            setItems(items)
            setTimeRange({
              from: parseDate(items[0]),
              to: lastDayOfMonth(parseDate(items[-ls - 1]))
            })
          } else {
            setHidden(true)
            return Promise.reject(data)
          }
        })
    }
  }, [admin_district, selected, setTimeRange])
  return items
}

const RegionalData = (props) => {
  const postcode = props.postcode
  const instance = React.useRef()
  const wrapperRef = React.useRef()
  const tooltipRef = React.useRef()
  const ref = React.useRef()
  const dimensions = useResizeObserver(wrapperRef)
  const [growth, setGrowth] = React.useState({ annual: null, total: null })
  const [timeRangeInput, setTimeRangeInput] = React.useState('last_5years')
  const [timeRange, setTimeRange] = React.useState({ from: null, to: null })
  const [isHidden, setHidden] = React.useState(false)
  const region = useRegionOfPostcode(postcode, setHidden)

  function changeTimeRangeInput(ev) {
    const selected = ev?.target?.value ?? 'last_year'
    setTimeRangeInput(selected)
  }

  const items = useUKHPIDataItems(region.admin_district, timeRangeInput, setTimeRange, setHidden)

  React.useLayoutEffect(() => {
    if (dimensions && items?.length && timeRange.to && timeRange.from) {
      const { width, height } = dimensions

      const yearStep = 12 / (1 + differenceInMonths(startOfMonth(timeRange.to), timeRange.from))

      const p0 = items[0]?.['ukhpi:averagePrice'][0]
      const plast = items[items.length - 1]['ukhpi:averagePrice'][0]
      const totalGrowth = plast / p0
      setGrowth({ annual: Math.pow(totalGrowth, yearStep) - 1, total: totalGrowth - 1 })
      if (width && height ) {
        const hpi = items.map((n, i) => {
          const pRat = n['ukhpi:averagePrice'][0] / p0
          const _hpi = pRat - 1
          return _hpi
        })

        const hpiTickCount = 4
        const hpiScaleStepSize = 0.025
        const hpiRange = [Math.min(...hpi), Math.max(...hpi)]
        const lb = Math.floor(Math.min(...hpi) / hpiScaleStepSize) * hpiScaleStepSize
        const hpiDomain = [
          Math.min(lb , hpiScaleStepSize),
          Math.ceil(Math.max(...hpi) / hpiScaleStepSize) * hpiScaleStepSize
        ]
        const hpiScale = scaleLinear()
          .domain(hpiDomain)
          .range([dimensions?.height, 0])
          .clamp(true)

        const monthTickcount = 7
        const monthScale = scaleTime()
          .domain([parseDate(items[0]), lastDayOfMonth(parseDate(items[items.length - 1]))])
          .range([0, dimensions?.width])
          .clamp(true)

        const labelDates = Array.from(Array(monthTickcount), (_, i) => {
          switch (i) {
          case 0:
            return timeRange.from
          case monthTickcount - 1:
            return timeRange.to
          default:
            return interpolateDate(timeRange.from, timeRange.to)(i / (monthTickcount - 1))
          }
        })
        const today = new Date()
        const monthAxis = axisBottom(monthScale)
          .tickValues(labelDates)
          .tickFormat((x, i) => {
            return format(x, 'MMM \'\'yy ')
          })
        const yAxis = axisRight(hpiScale)
          .ticks(hpiTickCount)
          .tickFormat(formatPercentage)
          .tickSize(width)

        // chart components
        function addScales(chart) {
          const xaxis = chart.select('.x-axis')

          xaxis.style('transform', `translateY(${dimensions?.height}px)`).call(monthAxis)

          // ie11 hack     transform= getComputedStyle(g).getPropertyValue('transform');
          const g = xaxis.node()
          g.setAttribute('transform', getComputedStyle(g).getPropertyValue('transform'))

          chart
            .select('.y-axis')
            .style('transform', 'translateX(0px)')
            .call(yAxis)
            .call((g) => g.select('.domain').remove())
            .call((g) =>
              g
                .selectAll('.tick line')
                .attr('stroke-opacity', 0.3)
                .attr('stroke-dasharray', '1,4')
            )
            .call((g) =>
              g
                .selectAll('.tick text')
                .attr('x', 4)
                .attr('dy', -4)
            )
        }
        // labels ----------------------------------------------------------------------------------------
        function addLabels(chart) {
          const ps = chart.select('g.priceAverage')
          const z = ps
            .selectAll('.label')
            .attr('text-anchor', 'middle')
            .attr('y', 35)
            .attr('x', width * 0.8)
        }

        // UI -----------------------------------------------------------------------------

        function addInteraction(chart) {
          chart.on('mousemove touchmove', function() {
            const x = Math.min(event?.offsetX ?? event.layerX, width)
            const y = 0

            const label = chart
              .selectAll('.priceAverage')
              .select('.label')
              .attr('text-anchor', 'left')
              .attr('x', x + 10)
              .attr('y', y + 10)

            const ui = chart.select('.UI')
            const mi = ui.select('.monthIndicator')
            // eslint-disable-next-line no-unsanitized/method
            const currentMonth = monthScale.invert(x)

            const d = currentMonth
            const currentMonth_X = monthScale(d)
            const diff = differenceInMonths(currentMonth, parseDate(items[0]))
            const idx = Math.min(Math.max(0, diff), items.length - 1)
            const currenthpi = items[idx] && hpi[idx]
            const hpiY = hpiScale(currenthpi)

            mi.select('line')
              .attr('x1', currentMonth_X)
              .attr('y1', 0)
              .attr('x2', currentMonth_X)
              .attr('y2', height)
              .attr('style', 'stroke:rgba(170,170,170, 1);stroke-width:2')
              .attr('stroke-dasharray', '2,2')

            const pi = ui.select('.hpiIndicator')

            const tooltipContent = `<div class="Text -smaller"><strong>${formatPercentage(
              currenthpi
            )}</strong> HPI on ${format(currentMonth, 'MMMM yyyy')}
            <br /><strong>${formatPercentage(
              (items[idx]?.['ukhpi:percentageChange']?.[0] ?? 0)/ 100
            )}</strong> Monthly Change </div>`

            const z = select(wrapperRef.current)
            const tt = z.select('.chartTooltip')
            const ttRect = tt.node().getBoundingClientRect()
            const hw = width / 2
            const pos = currentMonth_X / width
            const shift = ttRect.width * (0.5 - pos)
            tt.style('margin-top', `${-ttRect.height + 4}px`)
              .style('transform', `translate(${currentMonth_X - hw + shift}px, ${0}px)`)
              .html(tooltipContent)
          })
        }

        // configuration ----------------------------------------------------------------------------------------
        const chart = select(ref.current)
        addScales(chart)
        addLabels(chart)
        addInteraction(chart)

        const hpiLine = line()
          .x((v, i) => {
            const r = i / items.length
            const d = parseDate(items[i])
            return monthScale(addDays(d, Math.round(r * getDaysInMonth(d))))
          })
          .y((v, i) => {
            return hpiScale(v)
          })
        chart
          .selectAll('.hpiLine')
          .data([hpi])
          .join('path')
          .attr('class', 'hpiLine')
          .attr('d', hpiLine)
          .attr('fill', 'none')
          .attr('stroke', '#FF5D5C')
          .attr('stroke-width', '3px')
      }
    }
  }, [ref, wrapperRef, items, dimensions, timeRangeInput, timeRange.from, timeRange.to])
  return (
    postcode && (
      <div style={{opacity: `${items ? '1' : '0'}`, display: `${isHidden ? 'none' : 'block'}`}}>
        <h5 className="Heading -info">House Price Index</h5>
        <h3 className="Heading -three -underline">{region.admin_district} Land Registry Data</h3>
        <div className="-mod-margin-top -mod-margin-bottom">
          <div className="Field ">
            <div className="Control">
              <div className="Row -evenly">
                <button
                  className={`Button -inverted ${pcls('-secondary', 'last_year', timeRangeInput)}`}
                  type="button"
                  name="buy"
                  value="last_year"
                  onClick={changeTimeRangeInput}
                >
                  Last year
                </button>
                &nbsp;
                <button
                  className={`Button -inverted ${pcls(
                    '-secondary',
                    'last_5years',
                    timeRangeInput
                  )}`}
                  type="button"
                  name="buy"
                  value="last_5years"
                  onClick={changeTimeRangeInput}
                >
                  Last 5 years
                </button>
                &nbsp;
                <button
                  className={`Button -inverted ${pcls('-secondary', 'all_data', timeRangeInput)}`}
                  type="button"
                  name="buy"
                  value="all_data"
                  onClick={changeTimeRangeInput}
                >
                  All data
                </button>
              </div>
            </div>
          </div>

          <div className="Text -mod-text-centered -mod-margin-top">
            Annualised growth: {formatPercentage(growth?.annual)}
          </div>
          <div className="Text -mod-text-centered -mod-margin-bottom">
            Total growth: {formatPercentage(growth?.total)}
          </div>
        </div>
        <div
          ref={wrapperRef}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'top',
            height: '300px',
            padding: '2rem',
            outline: '1px dashed #f0f0df'
          }}
        >
          <div
            style={{
              position: 'relative',
              pointerEvents: 'none',
              padding: '2px',
              background: 'white',
              outline: '2px dotted #D9F6FF'
            }}
            className="chartTooltip"
            ref={tooltipRef}
          ></div>
          <svg
            style={{
              width: '100%',
              display: 'block',
              overflow: 'visible',
              background: 'white',
              height: '100%'
            }}
            ref={ref}
          >
            <g className="x-axis" />
            <g className="y-axis" />
            <g className="vert-grid" />
            <g className="months" />
            <g className="scatterplot" />
            <g className="hpi">
              <text className="label "></text>
            </g>
            <g className="UI">
              <g className="monthIndicator">
                <line></line>
                <text className="label"></text>
              </g>
              <g className="hpiIndicator"></g>
            </g>
          </svg>
        </div>
      </div>
    )
  )
}
export default RegionalData
